// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { AuthProvider, useAuth } from './AuthContext';

import Login from './Login';
import Disclaimer from './Disclaimer';
import QuickQuestion from './QuickQuestion';
import BoardQuestionnaire from './components/BoardQuestionnaire';
import DiceRoll from './DiceRoll';
import UserProfile from './UserProfile';
import AchievementModal from './components/AchievementModal';
import EveningReflection from './EveningReflection';
import PushNotificationPrompt from './PushNotificationPrompt';
import { checkMilestone, getExtensionDays } from './utils/achievementUtils';

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', background: '#0D0D2B',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '16px'
    }}>
      <div style={{ fontSize: '48px' }}>🎲</div>
      <p style={{ fontFamily: '"Cinzel", serif', color: '#C9920A', fontSize: '16px', letterSpacing: '2px', margin: 0 }}>
        MOKSHAPATRA
      </p>
    </div>
  );
}

function AppContent() {
  const { currentUser, loading: authLoading } = useAuth();

  const [appScreen, setAppScreen] = useState('loading');
  const [showDashboard, setShowDashboard] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  const [introAnswer, setIntroAnswer] = useState(null);
  const [boardProfile, setBoardProfile] = useState(null);
  const [currentSquare, setCurrentSquare] = useState(0);
  const [hasPersonalizedBoard, setHasPersonalizedBoard] = useState(false);
  const [pendingAchievement, setPendingAchievement] = useState(null);
  const [existingMilestones, setExistingMilestones] = useState([]);

  // Load state from Firestore on auth change
  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) { setAppScreen('login'); return; }

    const load = async () => {
      try {
        const [userSnap, progressSnap] = await Promise.all([
          getDoc(doc(db, 'users', currentUser.uid)),
          getDoc(doc(db, 'users', currentUser.uid, 'progress', 'data')),
        ]);

        const u = userSnap.exists() ? userSnap.data() : {};
        const p = progressSnap.exists() ? progressSnap.data() : {};

        if (!u.disclaimerAccepted) { setAppScreen('disclaimer'); return; }
        if (!p.introAnswered)      { setAppScreen('intro');      return; }

        setCurrentSquare(p.currentSquare ?? 0);
        setExistingMilestones(p.milestonesAwarded ?? []);
        if (p.boardProfile) {
          setBoardProfile(p.boardProfile);
          setHasPersonalizedBoard(true);
        }
        if (p.introAnswer) setIntroAnswer(p.introAnswer);
        setAppScreen('game');
      } catch (err) {
        console.error('Error loading user state:', err);
        setAppScreen('game');
      }
    };

    load();
  }, [currentUser, authLoading]);

  const handleDisclaimerAccept = async () => {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        disclaimerAccepted: true,
        disclaimerAcceptedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Disclaimer save error:', err);
    }
    setAppScreen('intro');
  };

  const handleRollAndExplore = async (answer) => {
    setIntroAnswer(answer);
    try {
      await setDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'), {
        introAnswer: answer,
        introAnswered: true,
        introPath: 'explore',
        introAnsweredAt: new Date().toISOString(),
      }, { merge: true });
    } catch (err) {
      console.error('Intro save error:', err);
    }
    setAppScreen('game');
  };

  const handleDesignBoard = async (answer) => {
    setIntroAnswer(answer);
    try {
      await setDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'), {
        introAnswer: answer,
        introAnswered: true,
        introPath: 'questionnaire',
        introAnsweredAt: new Date().toISOString(),
      }, { merge: true });
    } catch (err) {
      console.error('Intro save error:', err);
    }
    setAppScreen('questionnaire');
  };

  const handleQuestionnaireComplete = async (profile) => {
    setBoardProfile(profile);
    setHasPersonalizedBoard(true);
    setCurrentSquare(0);
    try {
      await Promise.all([
        setDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'), {
          boardProfile: profile,
          boardPersonalized: true,
          personalizedAt: new Date().toISOString(),
          currentSquare: 0,
        }, { merge: true }),
        // Clear old board so DiceRoll generates a fresh one for the new profile
        setDoc(doc(db, 'users', currentUser.uid, 'board', 'data'), {}),
      ]);
    } catch (err) {
      console.error('Questionnaire save error:', err);
    }
    setAppScreen('game');
  };

  const handlePracticeComplete = async (newSquare, streakData) => {
    setCurrentSquare(newSquare);
    const milestonesAwarded = streakData.milestonesAwarded ?? existingMilestones;
    const milestone = checkMilestone(streakData.streakCount, milestonesAwarded);
    if (milestone) {
      const updatedMilestones = [...milestonesAwarded, milestone];
      setExistingMilestones(updatedMilestones);
      setPendingAchievement(milestone);
      try {
        await setDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'), {
          milestonesAwarded: updatedMilestones,
          pendingExtension: {
            milestone,
            days: getExtensionDays(milestone),
            awardedAt: new Date().toISOString(),
          },
        }, { merge: true });
      } catch (err) {
        console.error('Milestone save error:', err);
      }
    }
  };

  if (authLoading || appScreen === 'loading') return <LoadingScreen />;
  if (appScreen === 'login')       return <Login />;
  if (appScreen === 'disclaimer')  return <Disclaimer onAccept={handleDisclaimerAccept} />;
  if (appScreen === 'intro')       return <QuickQuestion onExplore={handleRollAndExplore} onDesignBoard={handleDesignBoard} />;
  if (appScreen === 'questionnaire') return <BoardQuestionnaire introAnswer={introAnswer} onComplete={handleQuestionnaireComplete} />;

  return (
    <>
      <DiceRoll
        boardProfile={boardProfile}
        hasPersonalizedBoard={hasPersonalizedBoard}
        currentSquare={currentSquare}
        onPracticeComplete={handlePracticeComplete}
        onOpenDashboard={() => setShowDashboard(true)}
        onGoToQuestionnaire={() => setAppScreen('questionnaire')}
        onOpenReflection={() => setShowReflection(true)}
      />
      {showDashboard && (
        <UserProfile
          currentSquare={currentSquare}
          hasPersonalizedBoard={hasPersonalizedBoard}
          onClose={() => setShowDashboard(false)}
          onGoToQuestionnaire={() => { setShowDashboard(false); setAppScreen('questionnaire'); }}
        />
      )}
      {showReflection && <EveningReflection onClose={() => setShowReflection(false)} />}
      {showPushPrompt && <PushNotificationPrompt onDismiss={() => setShowPushPrompt(false)} />}
      {pendingAchievement && (
        <AchievementModal
          milestone={pendingAchievement}
          onDismiss={() => setPendingAchievement(null)}
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
